import type { DirectedVideoStatus, SerializedDirectedVideo } from 'fym-shared/src/music24/directedVideoApi';

/**
 * The screens of the wizard (design directed-video b to g). The first five are the steps of the
 * stepper that an artist can open again; `progress` shows making, rendering and ready (g1 to g3),
 * `failed` shows g4.
 */
export const DIRECTED_WIZARD_STEPS = ['cast', 'stories', 'style', 'script', 'price', 'progress'] as const;
export type DirectedWizardStep = (typeof DIRECTED_WIZARD_STEPS)[number];

/** A screen of the wizard: a step, or the end states. */
export type DirectedScreen = DirectedWizardStep | 'failed' | 'cancelled';

/** The steps that the `?step=` parameter may open again (the steps before the make). */
const REVISIT_STEPS: readonly DirectedWizardStep[] = ['cast', 'stories', 'style', 'script', 'price'];

/**
 * The steps that the artist may open to read after the make started (owner report 2026-10-18: "the
 * user can read the script"). They open read only. The price is not one: its GET prices a script
 * that is still edited; the result view shows what was charged.
 */
export const DIRECTED_READ_AFTER_MAKE_STEPS: readonly DirectedWizardStep[] = ['cast', 'stories', 'style', 'script'];

/**
 * The statuses of a make that still runs. `ready` is not one: a ready video is history, and the
 * entry makes a new video (owner report 2026-10-19: the entry opened the ready video again).
 */
const MAKING_STATUSES: readonly DirectedVideoStatus[] = ['making', 'rendering'];

/** The statuses in which the artist still edits the video. */
export const DIRECTED_EDIT_STATUSES: readonly DirectedVideoStatus[] = ['cast', 'stories', 'style', 'script', 'priced'];

/**
 * The step of a directed video from its data (spec §4): the status, and the choice the artist made
 * in it. A picked story moves the video to the style step (the director writes the styles there),
 * a picked style to the script step. A run of the director on the cast step writes the stories,
 * so it shows the stories step. Pure.
 */
export function directedScreenOf(
  video: Pick<SerializedDirectedVideo, 'status' | 'story' | 'style' | 'directorState'>
): DirectedScreen {
  switch (video.status) {
    case 'cast':
      return video.directorState === 'working' ? 'stories' : 'cast';
    case 'stories':
      return video.story === null ? 'stories' : 'style';
    case 'style':
      return video.style === null ? 'style' : 'script';
    case 'script':
      return 'script';
    case 'priced':
      return 'price';
    case 'making':
    case 'rendering':
    case 'ready':
      return 'progress';
    case 'failed':
      return 'failed';
    case 'cancelled':
      return 'cancelled';
    default:
      return 'cast';
  }
}

/** The place of a screen in the six-step stepper (0 = Cast, 5 = Make). Pure. */
export function directedStepIndex(screen: DirectedScreen): number {
  if (screen === 'failed' || screen === 'cancelled') {
    return DIRECTED_WIZARD_STEPS.length - 1;
  }
  return DIRECTED_WIZARD_STEPS.indexOf(screen);
}

/**
 * True when the screen on view is a step that the artist only reads: an earlier step, opened after
 * the make started. Every write of the step is off then. Pure.
 */
export function isDirectedStepReadOnly(current: DirectedScreen, shown: DirectedScreen): boolean {
  return current === 'progress' && shown !== 'progress';
}

/** Reads the `?step=` of "How it was made": any step of the wizard, else null. Pure. */
export function parseMadeStepParam(value: string | null): DirectedWizardStep | null {
  return value !== null && (DIRECTED_WIZARD_STEPS as readonly string[]).includes(value)
    ? (value as DirectedWizardStep)
    : null;
}

/** Reads `?step=`: a step name, else null. Pure. */
export function parseDirectedStepParam(value: string | null): DirectedWizardStep | null {
  return value !== null && (REVISIT_STEPS as readonly string[]).includes(value) ? (value as DirectedWizardStep) : null;
}

/**
 * The screen to show: the step of `?step=` when the artist may open it again (a step before the
 * current one, while the video is still edited; after the make started, a step of
 * `DIRECTED_READ_AFTER_MAKE_STEPS`, read only), else the step of the data. Pure.
 */
export function shownDirectedScreen(current: DirectedScreen, requested: DirectedWizardStep | null): DirectedScreen {
  if (requested === null || current === 'failed' || current === 'cancelled') {
    return current;
  }
  if (current === 'progress') {
    return DIRECTED_READ_AFTER_MAKE_STEPS.includes(requested) ? requested : current;
  }
  return directedStepIndex(requested) < directedStepIndex(current) ? requested : current;
}

/**
 * The directed video that the entry and a link with no uuid open: the newest one that is still
 * edited or being made. A ready, failed or cancelled video is history: the entry starts a new one.
 * `videos` is newest first, as `GET .../directed` answers. Pure.
 */
export function openDirectedVideoOf(videos: readonly SerializedDirectedVideo[]): SerializedDirectedVideo | null {
  return (
    videos.find((video) => DIRECTED_EDIT_STATUSES.includes(video.status) || MAKING_STATUSES.includes(video.status)) ??
    null
  );
}

/**
 * True while the screen waits for a change on the server: the director writes, the make runs, or a
 * cast picture is being made. Then the wizard polls, but only while the socket is down. Pure.
 */
export function isDirectedVideoWaiting(video: SerializedDirectedVideo): boolean {
  return (
    video.directorState === 'working' ||
    video.status === 'making' ||
    video.status === 'rendering' ||
    video.castPicturesPending.some((picture) => picture.status === 'working')
  );
}
